/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Typography } from '@mui/material';
import React from 'react';
// Module https://www.npmjs.com/package/react-webcam
// Source https://github.com/mozmorris/react-webcam
// License MIT
// polyfill based on https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
(function polyfillGetUserMedia() {
    if (typeof window === 'undefined') {
        return;
    }
    // Older browsers might not implement mediaDevices at all, so we set an empty object first
    if (navigator.mediaDevices === undefined) {
        navigator.mediaDevices = {};
    }
    // Some browsers partially implement mediaDevices. We can't just assign an object
    // with getUserMedia as it would overwrite existing properties.
    // Here, we will just add the getUserMedia property if it's missing.
    if (navigator.mediaDevices.getUserMedia === undefined) {
        navigator.mediaDevices.getUserMedia = function (constraints) {
            // First get ahold of the legacy getUserMedia, if present
            const getUserMedia = navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia;
            // Some browsers just don't implement it - return a rejected promise with an error
            // to keep a consistent interface
            if (!getUserMedia) {
                return Promise.reject(new Error('getUserMedia is not implemented in this browser'));
            }
            // Otherwise, wrap the call to the old navigator.getUserMedia with a Promise
            return new Promise(function (resolve, reject) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                getUserMedia.call(navigator, constraints, resolve, reject);
            });
        };
    }
})();
function hasGetUserMedia() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}
/**
 * A React component for capturing webcam video and taking screenshots.
 * Supports audio/video constraints, mirroring, image smoothing, forced source-size screenshots,
 * and minimum screenshot dimensions. Also provides hooks for handling
 * media stream acquisition and errors.
 *
 * @class
 * @augments React.Component
 * @param {WebcamProps} props - Webcam configuration props.
 * @param {boolean} props.audio - Whether to capture audio.
 * @param {MediaStreamConstraints['audio']} [props.audioConstraints] - Constraints for audio capture.
 * @param {boolean} props.forceScreenshotSourceSize - Whether to use the video source's native size for screenshots.
 * @param {boolean} props.imageSmoothing - Whether to enable image smoothing on the screenshot canvas.
 * @param {boolean} props.mirrored - Whether to horizontally mirror the video and screenshots.
 * @param {number} [props.minScreenshotHeight] - Minimum screenshot height (px).
 * @param {number} [props.minScreenshotWidth] - Minimum screenshot width (px).
 * @param {(stream: MediaStream) => void} props.onUserMedia - Callback when media stream is successfully acquired.
 * @param {(error: string) => void} props.onUserMediaError - Callback when acquiring media stream fails.
 * @param {'image/webp'|'image/png'|'image/jpeg'} props.screenshotFormat - Screenshot image format.
 * @param {number} props.screenshotQuality - Screenshot quality (0–1, applicable for lossy formats).
 * @param {number} [props.borderRadius] - CSS border-radius for video element (px).
 * @param {number} [props.maxHeight] - Maximum video element height (px).
 * @param {number} [props.maxWidth] - Maximum video element width (px).
 * @param {MediaStreamConstraints['video']} [props.videoConstraints] - Constraints for video capture.
 * @param {(childrenProps: ChildrenProps) => React.ReactNode} [props.children] - Render-prop to access screenshot API.
 *
 * @returns {JSX.Element} The webcam video element and optional loading message.
 */
export class Webcam extends React.Component {
    /**
     * Creates an instance of the Webcam component.
     *
     * @param {WebcamProps} props - The props to configure the webcam component.
     */
    constructor(props) {
        super(props);
        this.canvas = null;
        this.ctx = null;
        this.requestUserMediaId = 0;
        this.unmounted = false;
        this.state = {
            hasUserMedia: false,
        };
        this.stream = null;
        this.video = null;
    }
    componentDidMount() {
        const { state, props } = this;
        this.unmounted = false;
        if (!hasGetUserMedia()) {
            props.onUserMediaError('getUserMedia not supported');
            return;
        }
        if (!state.hasUserMedia) {
            this.requestUserMedia();
        }
        if (props.children && typeof props.children != 'function') {
            console.warn('children must be a function');
        }
    }
    /**
     * Checks for constraint changes and re-requests the media stream if necessary.
     *
     * @param {WebcamProps} nextProps - The new props after the update.
     * @returns {void}
     */
    componentDidUpdate(nextProps) {
        const { props } = this;
        if (!hasGetUserMedia()) {
            props.onUserMediaError('getUserMedia not supported');
            return;
        }
        const audioConstraintsChanged = JSON.stringify(nextProps.audioConstraints) !== JSON.stringify(props.audioConstraints);
        const videoConstraintsChanged = JSON.stringify(nextProps.videoConstraints) !== JSON.stringify(props.videoConstraints);
        const minScreenshotWidthChanged = nextProps.minScreenshotWidth !== props.minScreenshotWidth;
        const minScreenshotHeightChanged = nextProps.minScreenshotHeight !== props.minScreenshotHeight;
        if (videoConstraintsChanged || minScreenshotWidthChanged || minScreenshotHeightChanged) {
            this.canvas = null;
            this.ctx = null;
        }
        if (audioConstraintsChanged || videoConstraintsChanged) {
            this.stopAndCleanup();
            this.requestUserMedia();
        }
    }
    componentWillUnmount() {
        this.unmounted = true;
        this.stopAndCleanup();
    }
    static stopMediaStream(stream) {
        if (stream) {
            if (stream.getVideoTracks && stream.getAudioTracks) {
                stream.getVideoTracks().map((track) => {
                    stream.removeTrack(track);
                    track.stop();
                });
                stream.getAudioTracks().map((track) => {
                    stream.removeTrack(track);
                    track.stop();
                });
            }
            else {
                stream.stop();
            }
        }
    }
    stopAndCleanup() {
        const { state } = this;
        if (state.hasUserMedia) {
            Webcam.stopMediaStream(this.stream);
            if (state.src) {
                window.URL.revokeObjectURL(state.src);
            }
        }
    }
    /**
     * Captures a screenshot from the webcam video stream.
     *
     * @param {ScreenshotDimensions} [screenshotDimensions] - Optional width and height to override the default screenshot size.
     * @returns {string|null} A base64-encoded image string in the specified format, or null if capture is not possible.
     */
    getScreenshot(screenshotDimensions) {
        const { state, props } = this;
        if (!state.hasUserMedia)
            return null;
        const canvas = this.getCanvas(screenshotDimensions);
        return canvas && canvas.toDataURL(props.screenshotFormat, props.screenshotQuality);
    }
    /**
     * Returns a canvas element containing the current video frame.
     *
     * @param {ScreenshotDimensions} [screenshotDimensions] - Optional width and height to override the default canvas size.
     * @returns {HTMLCanvasElement|null} The populated canvas element, or null if no video frame is available.
     */
    getCanvas(screenshotDimensions) {
        const { state, props } = this;
        if (!this.video) {
            return null;
        }
        if (!state.hasUserMedia || !this.video.videoHeight)
            return null;
        if (!this.ctx) {
            let canvasWidth = this.video.videoWidth;
            let canvasHeight = this.video.videoHeight;
            if (!this.props.forceScreenshotSourceSize) {
                const aspectRatio = canvasWidth / canvasHeight;
                canvasWidth = props.minScreenshotWidth || this.video.clientWidth;
                canvasHeight = canvasWidth / aspectRatio;
                if (props.minScreenshotHeight && canvasHeight < props.minScreenshotHeight) {
                    canvasHeight = props.minScreenshotHeight;
                    canvasWidth = canvasHeight * aspectRatio;
                }
            }
            this.canvas = document.createElement('canvas');
            this.canvas.width = (screenshotDimensions === null || screenshotDimensions === void 0 ? void 0 : screenshotDimensions.width) || canvasWidth;
            this.canvas.height = (screenshotDimensions === null || screenshotDimensions === void 0 ? void 0 : screenshotDimensions.height) || canvasHeight;
            this.ctx = this.canvas.getContext('2d');
        }
        const { ctx, canvas } = this;
        if (ctx && canvas) {
            // mirror the screenshot
            if (props.mirrored) {
                ctx.translate(canvas.width, 0);
                ctx.scale(-1, 1);
            }
            ctx.imageSmoothingEnabled = props.imageSmoothing;
            ctx.drawImage(this.video, 0, 0, (screenshotDimensions === null || screenshotDimensions === void 0 ? void 0 : screenshotDimensions.width) || canvas.width, (screenshotDimensions === null || screenshotDimensions === void 0 ? void 0 : screenshotDimensions.height) || canvas.height);
            // invert mirroring
            if (props.mirrored) {
                ctx.scale(-1, 1);
                ctx.translate(-canvas.width, 0);
            }
        }
        return canvas;
    }
    requestUserMedia() {
        const { props } = this;
        const sourceSelected = (audioConstraints, videoConstraints) => {
            const constraints = {
                video: typeof videoConstraints !== 'undefined' ? videoConstraints : true,
            };
            if (props.audio) {
                constraints.audio = typeof audioConstraints !== 'undefined' ? audioConstraints : true;
            }
            this.requestUserMediaId++;
            const myRequestUserMediaId = this.requestUserMediaId;
            navigator.mediaDevices
                .getUserMedia(constraints)
                .then((stream) => {
                if (this.unmounted || myRequestUserMediaId !== this.requestUserMediaId) {
                    Webcam.stopMediaStream(stream);
                }
                else {
                    this.handleUserMedia(null, stream);
                }
            })
                .catch((e) => {
                this.handleUserMedia(e);
            });
        };
        if ('mediaDevices' in navigator) {
            sourceSelected(props.audioConstraints, props.videoConstraints);
        }
        else {
            const optionalSource = (id) => ({
                optional: [{ sourceId: id }],
            });
            const constraintToSourceId = (constraint) => {
                const { deviceId } = constraint;
                if (typeof deviceId === 'string') {
                    return deviceId;
                }
                if (Array.isArray(deviceId) && deviceId.length > 0) {
                    return deviceId[0];
                }
                if (typeof deviceId === 'object' && deviceId.ideal) {
                    return deviceId.ideal;
                }
                return null;
            };
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore: deprecated api
            MediaStreamTrack.getSources((sources) => {
                let audioSource = null;
                let videoSource = null;
                sources.forEach((source) => {
                    if (source.kind === 'audio') {
                        audioSource = source.id;
                    }
                    else if (source.kind === 'video') {
                        videoSource = source.id;
                    }
                });
                const audioSourceId = constraintToSourceId(props.audioConstraints);
                if (audioSourceId) {
                    audioSource = audioSourceId;
                }
                const videoSourceId = constraintToSourceId(props.videoConstraints);
                if (videoSourceId) {
                    videoSource = videoSourceId;
                }
                sourceSelected(optionalSource(audioSource), optionalSource(videoSource));
            });
        }
    }
    handleUserMedia(err, stream) {
        const { props } = this;
        if (err || !stream) {
            this.setState({ hasUserMedia: false });
            props.onUserMediaError(err.message);
            return;
        }
        this.stream = stream;
        try {
            if (this.video) {
                this.video.srcObject = stream;
            }
            this.setState({ hasUserMedia: true });
        }
        catch (error) {
            this.setState({
                hasUserMedia: true,
                src: window.URL.createObjectURL(stream),
            });
        }
        props.onUserMedia(stream);
    }
    /**
     * @returns {JSX.Element} The webcam video element and optional loading message.
     */
    render() {
        var _a, _b, _c;
        const { state, props } = this;
        const { audio, mirrored } = props;
        return (React.createElement(React.Fragment, null,
            !state.hasUserMedia ? React.createElement(Typography, null, "Loading...") : null,
            React.createElement(Box, { component: 'video', autoPlay: true, src: state.src, muted: !audio, playsInline: true, ref: (ref) => {
                    this.video = ref;
                }, sx: {
                    transform: mirrored ? `scaleX(-1)` : undefined,
                    borderRadius: (_a = props.borderRadius) !== null && _a !== void 0 ? _a : 0,
                    maxWidth: (_b = props.maxWidth) !== null && _b !== void 0 ? _b : 400,
                    maxHeight: (_c = props.maxHeight) !== null && _c !== void 0 ? _c : 400,
                } })));
    }
}
Webcam.defaultProps = {
    audio: false,
    forceScreenshotSourceSize: false,
    imageSmoothing: true,
    mirrored: false,
    onUserMedia: () => undefined,
    onUserMediaError: () => undefined,
    screenshotFormat: 'image/jpeg',
    screenshotQuality: 1,
};
